/**
 * SF-4845: a copy that stopped after the restore began but before the copied data was made safe
 * ("recovery_required"). The target stays stopped and locked until the copy is resumed. Resume
 * asks sf-api to start the runner again for this run (same edit access as starting a copy).
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Space } from 'antd';
import {
  envCopyErrorMessage,
  envCopyRunQueryKey,
  envCopyRunsQueryKey,
  putEnvCopyRun,
  resumeEnvCopyArgs,
  useCopyStartScope,
  useEnvCopyMutation,
  type EnvCopyRun,
} from '../api/envCopyClient';
import { EditScopeNotice } from './EditScopeNotice';

const DEFAULT_REASON =
  'The target is stopped and its copied data is not made safe yet. Resume the copy.';

interface ResumeCopyProps {
  run: EnvCopyRun;
  /** A resume was accepted and the page is waiting for the runner to pick the copy up. */
  resuming: boolean;
  /** The page stopped waiting: the runner did not pick the copy up in time. */
  notStarted: boolean;
  /** The run is being read again (Check again). */
  checking: boolean;
  /** A resume was accepted; `answered` is the run as the resume answer showed it. */
  onResumed: (answered: EnvCopyRun) => void;
  onCheckAgain: () => void;
}

export const ResumeCopy: React.FC<ResumeCopyProps> = ({
  run,
  resuming,
  notStarted,
  checking,
  onResumed,
  onCheckAgain,
}) => {
  const scope = useCopyStartScope();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useEnvCopyMutation<{ run: EnvCopyRun }>();
  const [problem, setProblem] = useState<string | null>(null);
  const busy = isLoading || resuming;

  const resume = () => {
    if (!scope.canStart || busy) return;
    setProblem(null);
    mutate(resumeEnvCopyArgs(run.run_id), {
      onSuccess: async (answer) => {
        const next = answer?.data?.run;
        onResumed(next?.run_id === run.run_id ? next : run);
        void queryClient.invalidateQueries({ queryKey: [...envCopyRunsQueryKey] });
        if (next?.run_id === run.run_id) {
          await putEnvCopyRun(queryClient, next);
        } else {
          void queryClient.invalidateQueries({ queryKey: envCopyRunQueryKey(run.run_id) });
        }
      },
      onError: (error) =>
        setProblem(envCopyErrorMessage(error, 'Could not resume the copy. Try again.')),
    });
  };

  return (
    <Alert
      type="warning"
      showIcon
      message="This copy needs resuming"
      description={
        <Space direction="vertical">
          <span>{run.error || DEFAULT_REASON}</span>
          <Button
            type="primary"
            aria-busy={busy}
            loading={busy}
            disabled={!scope.canStart || busy}
            onClick={resume}
          >
            Resume the copy
          </Button>
          {notStarted && !busy && (
            <Alert
              type="info"
              showIcon
              message="The copy has not started yet. It still needs resuming."
              action={
                <Button size="small" loading={checking} onClick={onCheckAgain}>
                  Check again
                </Button>
              }
            />
          )}
          <EditScopeNotice scope={scope} action="resuming a copy" />
          {problem && <Alert type="error" showIcon message={problem} />}
        </Space>
      }
    />
  );
};
